import React from 'react';

import { Badge, Box, Button, IconButton } from '@mui/material';
import { isEmpty } from 'es-toolkit/compat';

import { Popover } from 'src/components/common';
import { useFilter, useFilterStorage } from 'src/hooks/common';
import { DEFAULT_FILTER_CONTEXT_VALUE } from 'src/providers/FilterProvider';
import { PaginationContextValue } from 'src/providers/PaginationProvider';
import { SxProp } from 'src/types/mui';

import { TableFilters } from './TableFilters';
import type { TableFilter, TableFiltersGroup } from './types';

interface TableFiltersPopoverProps {
  filters: TableFilter[];
  filtersGroups: TableFiltersGroup[];
  actionIcon: React.ReactNode;
  disabled: boolean;
  isTrackFilter: boolean;
  pagination?: PaginationContextValue;
  keyFilter?: string;
}

export const TableFiltersPopover: React.FC<TableFiltersPopoverProps> = ({
  disabled,
  filters,
  filtersGroups,
  actionIcon,
  isTrackFilter,
  pagination,
  keyFilter,
}) => {
  const { setFilter, setTableFilter, filter } = useFilter();
  const {
    setFilter: setFilterByLocation,
    setFilterByKey,
    filter: filterByLocation,
    getFilterByKey,
  } = useFilterStorage();

  const onCancel = React.useCallback(() => {
    setFilter(DEFAULT_FILTER_CONTEXT_VALUE.filter);
    if (keyFilter) {
      setFilterByKey(keyFilter, DEFAULT_FILTER_CONTEXT_VALUE.filter);
    } else {
      setFilterByLocation(DEFAULT_FILTER_CONTEXT_VALUE.filter);
    }
    setTableFilter?.(DEFAULT_FILTER_CONTEXT_VALUE.tableFilter);
  }, [keyFilter, setFilter, setFilterByKey, setFilterByLocation, setTableFilter]);

  const renderTableFilters = React.useCallback(
    ({ onClose }: { onClose: () => void }) => {
      return (
        <TableFilters
          keyFilter={keyFilter}
          tableFilters={filters}
          filtersGroups={filtersGroups}
          closePopover={onClose}
          isTrackFilter={isTrackFilter}
          pagination={pagination}
        />
      );
    },
    [filters, filtersGroups, isTrackFilter, keyFilter, pagination],
  );

  const isFiltersEmpty = React.useMemo(
    () => isEmpty(filter) && isEmpty(filterByLocation) && isEmpty(getFilterByKey(keyFilter || '')),
    [filter, filterByLocation, getFilterByKey, keyFilter],
  );

  if (disabled) {
    // disabled button state doesn't work properly if button is passed as target prop
    return (
      <Box sx={containerStyles}>
        <IconButton disabled>{actionIcon}</IconButton>
        <Button disabled>Clear Filters</Button>
      </Box>
    );
  }

  return (
    <Box sx={containerStyles}>
      <Popover
        target={
          <IconButton sx={{ color: theme => theme.palette.action.active }}>
            <Badge
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              variant="dot"
              color="primary"
              invisible={isFiltersEmpty}
            >
              {actionIcon}
            </Badge>
          </IconButton>
        }
        content={renderTableFilters}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        elevation={4}
      />
      <Button onClick={onCancel}>Clear Filters</Button>
    </Box>
  );
};

const containerStyles: SxProp = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
};
